import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface GeneratedReport {
  reportId: string;
  reportType: 'platform_summary' | 'user_analytics' | 'booking_analytics' | 'revenue_analytics' | 'custom';
  title: string;
  description?: string;
  generatedBy: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  filters?: Record<string, unknown>;
  data: unknown;
  format: 'json' | 'csv';
  status: 'generating' | 'completed' | 'failed';
  createdAt: string;
  expiresAt?: string;
}

export interface GenerateReportRequest {
  reportType: 'platform_summary' | 'user_analytics' | 'booking_analytics' | 'revenue_analytics' | 'custom';
  title: string;
  description?: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  filters?: Record<string, unknown>;
  format?: 'json' | 'csv';
}

export interface ReportListResponse {
  reports: GeneratedReport[];
  count: number;
  lastEvaluatedKey?: string;
}

class ReportService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async generateReport(request: GenerateReportRequest): Promise<GeneratedReport> {
    const response = await this.api.post<{ data: { report: GeneratedReport } }>(
      '/admin/reports/generate',
      request
    );
    return response.data.data.report;
  }

  async listReports(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
    reportType?: string;
    generatedBy?: string;
  }): Promise<ReportListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.reportType) queryParams.append('reportType', params.reportType);
    if (params?.generatedBy) queryParams.append('generatedBy', params.generatedBy);

    const response = await this.api.get<{ data: ReportListResponse }>(
      `/admin/reports?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async getReport(reportId: string): Promise<GeneratedReport> {
    const response = await this.api.get<{ data: { report: GeneratedReport } }>(
      `/admin/reports/${reportId}`
    );
    return response.data.data.report;
  }

  exportToCSV(report: GeneratedReport): string {
    if (report.format === 'csv' && typeof report.data === 'string') {
      return report.data;
    }

    const data = report.data as Record<string, unknown>;
    const rows: string[] = [];

    if (Array.isArray(data)) {
      if (data.length === 0) return '';
      const headers = Object.keys(data[0] as Record<string, unknown>);
      rows.push(headers.join(','));
      data.forEach((item) => {
        const values = headers.map((h) => {
          const val = (item as Record<string, unknown>)[h];
          return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
        });
        rows.push(values.join(','));
      });
    } else {
      const entries = Object.entries(data);
      rows.push('Key,Value');
      entries.forEach(([key, value]) => {
        const val = typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : JSON.stringify(value);
        rows.push(`${key},${val}`);
      });
    }

    return rows.join('\n');
  }

  exportToJSON(report: GeneratedReport): string {
    return JSON.stringify(report.data, null, 2);
  }

  downloadFile(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const reportService = new ReportService();
